import { Button } from '@understory-io/leaf/button';
import { css } from '@understory-io/leaf/css';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { useId, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ReviewStar, ReviewStars } from './review-stars';

export type ReviewListItem = {
  id: string;
  reviewerName: string;
  starRating: number;
  comment: string;
  /** What was reviewed. Absent when the experience is no longer on the storefront. */
  experienceName?: string;
  /** Epoch milliseconds. */
  createdAt: number;
};

/** One bar of the breakdown: how many guests gave the company this many stars. */
export type StarBar = {
  stars: number;
  count: number;
};

type ReviewsSectionProps = {
  /** Out of five, already rounded to one decimal by the service. */
  starRating: number;
  /** Reviews carrying a rating — the number the average is drawn from. */
  count: number;
  /** Five bars, highest first, summing to `count`. */
  starCounts: StarBar[];
  reviews: ReviewListItem[];
  /**
   * When the page was rendered, from the loader rather than the clock, so the
   * server and the browser agree on "14h ago" and hydration is quiet.
   */
  now: number;
};

// How many reviews are on screen before the reader asks for more. The service
// answers with one page and no cursor, so the button reveals the rest of what
// is already here rather than fetching.
const INITIAL_COUNT = 5;

const SUMMARY_STAR_COUNT = 5;
const SUMMARY_STAR_SIZE = 24;
const REVIEW_STAR_SIZE = 20;
const BREAKDOWN_STAR_SIZE = 16;

export function ReviewsSection({
  starRating,
  count,
  starCounts,
  reviews,
  now,
}: ReviewsSectionProps) {
  const intl = useIntl();
  const headingId = useId();
  const [expanded, setExpanded] = useState(false);

  const visible = expanded ? reviews : reviews.slice(0, INITIAL_COUNT);
  const remaining = reviews.length - visible.length;

  return (
    <Stack as="section" gap="medium" aria-labelledby={headingId}>
      <Text as="h2" style="subheadline-large" id={headingId}>
        <FormattedMessage
          defaultMessage="Reviews"
          description="Heading of the reviews section on the home page"
        />
      </Text>

      <div className={wrapperStyles}>
        <Stack gap="2xlarge">
          <div className={summaryStyles}>
            <div className={summaryAverageStyles}>
              <Stack gap="medium" align="center">
                <Stack gap="small" align="center">
                  <Text as="p" style="headline">
                    {intl.formatNumber(starRating, {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}
                  </Text>

                  <div className={summaryStarsStyles} aria-hidden="true">
                    {Array.from({ length: SUMMARY_STAR_COUNT }, (_, index) => (
                      <span key={index} className={summaryStarTileStyles}>
                        <ReviewStar
                          filled={index < Math.round(starRating)}
                          size={SUMMARY_STAR_SIZE}
                        />
                      </span>
                    ))}
                  </div>
                </Stack>

                <Text as="p" style="body-large-regular" color="tertiary">
                  <FormattedMessage
                    defaultMessage="Average rating across {count, plural, one {# total review} other {# total reviews}}"
                    description="Caption under the average rating in the reviews section on the home page"
                    values={{ count }}
                  />
                </Text>
              </Stack>
            </div>

            <StarBreakdown starCounts={starCounts} total={count} />
          </div>

          <Stack gap="large">
            {visible.map((review) => (
              <ReviewCard key={review.id} review={review} now={now} />
            ))}
          </Stack>

          {remaining > 0 && (
            <Stack align="center">
              <Button appearance="secondary" onClick={() => setExpanded(true)}>
                <FormattedMessage
                  defaultMessage="Show {remaining, plural, one {# more review} other {# more reviews}}"
                  description="Action revealing the rest of the loaded reviews on the home page"
                  values={{ remaining }}
                />
              </Button>
            </Stack>
          )}
        </Stack>
      </div>
    </Stack>
  );
}

/**
 * A bar per star, highest first, each one that star's share of the reviews.
 *
 * The bars are the count printed above them broken apart — the service sends
 * all five and they sum to it — so the row reads as a whole rather than as a
 * sample. Hidden from assistive technology behind the summary it restates:
 * five bars read out as percentages is noise where "4.8 across 312 reviews"
 * has already been announced.
 */
function StarBreakdown({
  starCounts,
  total,
}: {
  starCounts: StarBar[];
  total: number;
}) {
  const intl = useIntl();

  if (starCounts.length === 0) {
    return null;
  }

  return (
    <div className={breakdownStyles} aria-hidden="true">
      {starCounts.map((bar) => (
        <div key={bar.stars} className={breakdownRowStyles}>
          <span className={breakdownStarStyles}>
            <Text as="span" style="body-large-bold">
              {intl.formatNumber(bar.stars)}
            </Text>
            <ReviewStar filled size={BREAKDOWN_STAR_SIZE} />
          </span>

          <span className={trackStyles}>
            <span
              className={fillStyles}
              style={{
                // Zero of zero is zero rather than NaN, though the section is
                // not rendered at all without a review behind it.
                width: `${total === 0 ? 0 : (bar.count / total) * 100}%`,
              }}
            />
          </span>

          <Text as="span" style="body-large-bold" color="secondary">
            {intl.formatNumber(bar.count)}
          </Text>
        </div>
      ))}
    </div>
  );
}

function ReviewCard({ review, now }: { review: ReviewListItem; now: number }) {
  const intl = useIntl();
  const initials = initialsOf(review.reviewerName);
  const { value, unit } = elapsed(review.createdAt, now);

  return (
    <article className={cardStyles}>
      <Stack gap="xlarge">
        <div className={cardHeaderStyles}>
          <div className={reviewerStyles}>
            {initials && (
              <span className={avatarStyles} aria-hidden="true">
                <Text as="span" style="label-tiny">
                  {initials}
                </Text>
              </span>
            )}

            <Stack gap="none">
              {review.reviewerName && (
                <Text as="p" style="body-large-bold">
                  {review.reviewerName}
                </Text>
              )}
              {review.experienceName && (
                <Text as="p" style="body-small-regular" color="tertiary">
                  {review.experienceName}
                </Text>
              )}
            </Stack>
          </div>

          <Stack gap="none" align={{ base: 'flex-start', tablet: 'flex-end' }}>
            <ReviewStars rating={review.starRating} size={REVIEW_STAR_SIZE} />
            {/*
             * Same clock on both sides isn't enough: Node and the browser ship
             * different locale data and word some of these differently
             * (Swedish "i går" vs Safari's "igår"). Unsuppressed, that mismatch
             * makes React throw the page away and render it again in the
             * browser. Either wording is right, so keep the server's.
             */}
            <Text
              as="p"
              style="body-small-regular"
              color="tertiary"
              suppressHydrationWarning
            >
              {intl.formatRelativeTime(value, unit, { numeric: 'auto' })}
            </Text>
          </Stack>
        </div>

        {review.comment && (
          <Text as="p" style="body-large-regular">
            {review.comment}
          </Text>
        )}
      </Stack>
    </article>
  );
}

/**
 * The guest is credited as a first name and a last initial, so the avatar takes
 * the first letter of each word. Empty for a booking that carried no name —
 * there is nothing to stand in for, and a blank circle is worse than none.
 */
function initialsOf(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Turns an age into the largest whole unit that describes it, for
 * `formatRelativeTime`. Negative because the review is in the past.
 *
 * Whole units only, and measured against the loader's clock rather than the
 * browser's: both server and browser then round to the same "14 hours ago", so
 * the markup matches on hydration.
 */
function elapsed(
  createdAt: number,
  now: number
): { value: number; unit: 'day' | 'hour' | 'minute' } {
  const age = Math.max(0, now - createdAt);

  if (age >= DAY) {
    return { value: -Math.floor(age / DAY), unit: 'day' };
  }
  if (age >= HOUR) {
    return { value: -Math.floor(age / HOUR), unit: 'hour' };
  }
  return { value: -Math.floor(age / MINUTE), unit: 'minute' };
}

// Side by side as the design has it: the average on the left, the breakdown on
// the right, each taking half. Below a tablet they stack, because five bars and
// a 32px average sharing a phone's width leaves neither readable.
const summaryStyles = css({
  display: 'flex',
  flexDirection: { base: 'column', tablet: 'row' },
  alignItems: 'center',
  gap: 'xlarge',
  width: 'full',
});

const summaryAverageStyles = css({
  flex: '[1 0 0]',
  minWidth: '[0]',
  width: 'full',
});

const breakdownStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'xsmall',
  flex: '[1 0 0]',
  minWidth: '[0]',
  width: 'full',
});

const breakdownRowStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'large',
  background: 'surface.base',
  border: 'medium',
  borderColor: 'border.neutral',
  borderRadius: 'small',
  paddingInline: 'medium',
  paddingBlock: 'small',
});

const breakdownStarStyles = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'micro',
  flexShrink: 0,
});

const trackStyles = css({
  flex: '[1 0 0]',
  minWidth: '[0]',
  height: '[10px]',
  borderRadius: 'full',
  background: 'surface.neutral',
  overflow: 'hidden',
});

const fillStyles = css({
  display: 'block',
  height: 'full',
  borderRadius: 'full',
  background: 'interactive.accent.default',
});

const wrapperStyles = css({
  background: 'surface.base',
  border: 'small',
  borderColor: 'border.neutral',
  borderRadius: 'large',
  padding: { base: 'large', tablet: '2xlarge' },
});

const summaryStarsStyles = css({
  display: 'flex',
  gap: 'tiny',
  alignItems: 'center',
});

// The tile is the design's 34px square: a 24px star with `tiny` padding inside
// a hairline border.
const summaryStarTileStyles = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'tiny',
  background: 'surface.accent',
  border: 'small',
  borderColor: 'border.neutral',
  borderRadius: 'xxsmall',
});

const cardStyles = css({
  background: 'surface.raised',
  border: 'small',
  borderColor: 'border.neutral',
  borderRadius: 'large',
  padding: 'large',
});

// Side by side where there is room for it, as the design has it. Narrower than
// a tablet the two halves stack instead: a name and an experience title sharing
// a phone's width with a row of stars wraps both to shreds.
const cardHeaderStyles = css({
  display: 'flex',
  flexDirection: { base: 'column', tablet: 'row' },
  alignItems: { base: 'flex-start', tablet: 'center' },
  justifyContent: 'space-between',
  gap: { base: 'small', tablet: 'medium' },
});

const reviewerStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'small',
  minWidth: '[0]',
});

const avatarStyles = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '[32px]',
  height: '[32px]',
  borderRadius: 'full',
  background: 'surface.accent',
  color: 'content.primary',
});
